import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Cpu, Camera, Router, CheckCircle2, AlertTriangle, Battery, ShieldAlert } from "lucide-react";
import api from "@/lib/api";

const ICON = { seat_sensor: Cpu, camera: Camera, gateway: Router };
const TYPE_LABEL = { seat_sensor: "Seat Sensor", camera: "Camera", gateway: "Gateway" };

export default function Devices() {
  const { data: devices } = useQuery({
    queryKey: ["devices"],
    queryFn: () => api.get("/devices/health").then((r) => r.data),
  });

  const healthy = (devices || []).filter((d) => d.status === "healthy").length;
  const attention = (devices || []).filter((d) => d.status !== "healthy").length;

  return (
    <div>
      <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">Device Health</div>
      <h1 className="mt-2 font-display text-3xl md:text-4xl font-light tracking-tight">
        Sensors, cameras & connectivity
      </h1>

      <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-5">
        <div className="surface-card p-6" data-testid="devices-healthy">
          <div className="flex items-center gap-2 text-emerald-400 text-xs uppercase tracking-[0.15em] font-semibold">
            <CheckCircle2 className="h-4 w-4" /> Healthy
          </div>
          <div className="mt-3 font-display text-3xl font-light">{healthy}</div>
        </div>
        <div className="surface-card p-6" data-testid="devices-attention">
          <div className="flex items-center gap-2 text-amber-400 text-xs uppercase tracking-[0.15em] font-semibold">
            <AlertTriangle className="h-4 w-4" /> Needs attention
          </div>
          <div className="mt-3 font-display text-3xl font-light">{attention}</div>
        </div>
        <div className="surface-card p-6">
          <div className="text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold">Total devices</div>
          <div className="mt-3 font-display text-3xl font-light">{devices?.length ?? "—"}</div>
        </div>
      </div>

      <div className="mt-6 surface-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="devices-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-slate-400 border-b border-[#2A3550]">
                <th className="px-5 py-4 font-semibold">Device</th>
                <th className="px-5 py-4 font-semibold">Type</th>
                <th className="px-5 py-4 font-semibold">Store</th>
                <th className="px-5 py-4 font-semibold">Location</th>
                <th className="px-5 py-4 font-semibold">Battery</th>
                <th className="px-5 py-4 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {(devices || []).map((d) => {
                const Icon = ICON[d.type] || Cpu;
                return (
                  <tr key={d.device_id} className="border-b border-[#2A3550] hover:bg-[#232C40] transition-colors duration-150">
                    <td className="px-5 py-3.5 font-mono text-xs text-slate-300">{d.device_id}</td>
                    <td className="px-5 py-3.5">
                      <span className="inline-flex items-center gap-2 text-slate-400">
                        <Icon className="h-4 w-4" /> {TYPE_LABEL[d.type] || d.type}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-slate-400">#{d.store_number}</td>
                    <td className="px-5 py-3.5 text-slate-400">{d.chair}</td>
                    <td className="px-5 py-3.5">
                      <span className="inline-flex items-center gap-1.5 text-slate-400">
                        <Battery className="h-4 w-4" /> {d.battery}%
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      {d.status === "healthy" ? (
                        <span className="inline-flex items-center gap-1.5 text-emerald-400">
                          <CheckCircle2 className="h-3.5 w-3.5" /> Healthy
                        </span>
                      ) : d.status === "tampered" ? (
                        <span className="inline-flex items-center gap-1.5 text-fuchsia-400">
                          <ShieldAlert className="h-3.5 w-3.5" /> Tampered
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-amber-400">
                          <AlertTriangle className="h-3.5 w-3.5" /> Needs attention
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
